import React, { type ComponentProps, useCallback, useEffect, useMemo } from 'react';
import { type LayoutChangeEvent, StyleSheet } from 'react-native';
import {
  Gesture,
  GestureDetector,
  type GestureStateChangeEvent,
  type GestureUpdateEvent,
  type PanGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import Animated, {
  Easing,
  FadeOut,
  interpolate,
  ReduceMotion,
  useAnimatedStyle,
  useDerivedValue,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import { theme } from '@garnish/constants';

import { NOTICE_BANNER_AUTO_HIDE_TIMEOUT, type NoticeBanner } from '../../../NoticeBanner';
import { NoticeToast } from '../../../NoticeToast';
import { useNoticeToastTimer } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const NoticeToastStackItem = (props: Props) => {
  const {
    toast,
    placement,
    deckOffset,
    deckScale,
    isCovered,
    isPaused,
    isLiquidGlassEnabled,
    onLayout,
    onDismiss,
    onInteractionStart,
    onInteractionEnd,
  } = props;

  const { autoHideTimeout = NOTICE_BANNER_AUTO_HIDE_TIMEOUT, withCloseBtn = true } = toast;

  const isReducedMotion = useReducedMotion();

  // ─── State ───────────────────────────────────────────────────────────

  const presence = useSharedValue(0);
  const swipeX = useSharedValue(0);
  const swipeY = useSharedValue(0);
  const swipeAxis = useSharedValue<SwipeAxis>('x');
  const toastWidth = useSharedValue(0);
  const toastHeight = useSharedValue(0);

  // ─── Derived ─────────────────────────────────────────────────────────

  const edgeDirection = placement === 'top' ? -1 : 1;
  const fullEnterDistance = isLiquidGlassEnabled ? GLASS_ENTER_DISTANCE : ENTER_DISTANCE;
  const enterDistance = isReducedMotion ? 0 : fullEnterDistance;
  const hiddenOpacity = isLiquidGlassEnabled ? 1 : 0;
  const isSwipeEnabled = withCloseBtn && !isCovered;

  const animatedDeckTranslateY = useDerivedValue(
    () => withTiming(deckOffset * -edgeDirection, LAYOUT_TIMING),
    [deckOffset, edgeDirection],
  );

  const animatedDeckScale = useDerivedValue(
    () => withTiming(deckScale, LAYOUT_TIMING),
    [deckScale],
  );

  // ─── Handlers ────────────────────────────────────────────────────────

  const removeToast = useCallback(() => {
    onDismiss(toast);
  }, [onDismiss, toast]);

  const handleExitEnd = useCallback(
    (isFinished?: boolean) => {
      'worklet';

      if (!isFinished) {
        return;
      }

      scheduleOnRN(removeToast);
    },
    [removeToast],
  );

  const show = useCallback(() => {
    presence.set(withTiming(1, ENTER_TIMING));
  }, [presence]);

  const dismiss = useCallback(() => {
    'worklet';

    presence.set(withTiming(0, EXIT_TIMING, handleExitEnd));
  }, [handleExitEnd, presence]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;

      toastWidth.set(width);
      toastHeight.set(height);
      onLayout(toast.id, height);
    },
    [onLayout, toast.id, toastHeight, toastWidth],
  );

  const handleSwipeStart = useCallback(
    (event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
      'worklet';

      const isHorizontal = Math.abs(event.translationX) >= Math.abs(event.translationY);

      swipeAxis.set(isHorizontal ? 'x' : 'y');
      scheduleOnRN(onInteractionStart);
    },
    [onInteractionStart, swipeAxis],
  );

  const handleSwipeUpdate = useCallback(
    (event: GestureUpdateEvent<PanGestureHandlerEventPayload>) => {
      'worklet';

      if (swipeAxis.get() === 'x') {
        swipeX.set(event.translationX);

        return;
      }

      const translationTowardEdge = Math.max(0, event.translationY * edgeDirection);

      swipeY.set(translationTowardEdge * edgeDirection);
    },
    [edgeDirection, swipeAxis, swipeX, swipeY],
  );

  const handleSwipeEnd = useCallback(
    (event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
      'worklet';

      const isHorizontal = swipeAxis.get() === 'x';
      const offset = isHorizontal ? swipeX.get() : swipeY.get();
      const size = isHorizontal ? toastWidth.get() : toastHeight.get();
      const velocityTowardEdge = Math.max(0, event.velocityY * edgeDirection);
      const velocity = isHorizontal ? Math.abs(event.velocityX) : velocityTowardEdge;
      const hasPassedDistance = Math.abs(offset) > size * SWIPE_DISMISS_DISTANCE_RATIO;
      const hasPassedVelocity = velocity > SWIPE_DISMISS_VELOCITY;

      if (!hasPassedDistance && !hasPassedVelocity) {
        swipeX.set(withTiming(0, LAYOUT_TIMING));
        swipeY.set(withTiming(0, LAYOUT_TIMING));

        return;
      }

      if (isHorizontal) {
        const swipeDirection = offset < 0 ? -1 : 1;

        swipeX.set(withTiming(swipeDirection * toastWidth.get(), EXIT_TIMING));
      }

      if (!isHorizontal) {
        swipeY.set(withTiming(edgeDirection * toastHeight.get(), EXIT_TIMING));
      }

      dismiss();
    },
    [dismiss, edgeDirection, swipeAxis, swipeX, swipeY, toastHeight, toastWidth],
  );

  const handleSwipeFinalize = useCallback(() => {
    'worklet';

    scheduleOnRN(onInteractionEnd);
  }, [onInteractionEnd]);

  const swipeGesture = useMemo(() => {
    const verticalActivationRange =
      placement === 'top' ? SWIPE_UP_ACTIVATION_RANGE : SWIPE_DOWN_ACTIVATION_RANGE;

    return Gesture.Pan()
      .enabled(isSwipeEnabled)
      .activeOffsetX(SWIPE_HORIZONTAL_ACTIVATION_RANGE)
      .activeOffsetY(verticalActivationRange)
      .onStart(handleSwipeStart)
      .onUpdate(handleSwipeUpdate)
      .onEnd(handleSwipeEnd)
      .onFinalize(handleSwipeFinalize);
  }, [
    handleSwipeEnd,
    handleSwipeFinalize,
    handleSwipeStart,
    handleSwipeUpdate,
    isSwipeEnabled,
    placement,
  ]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(show, [show]);
  useNoticeToastTimer({ duration: autoHideTimeout, isPaused, onTimeout: dismiss });

  // ─── Styles ──────────────────────────────────────────────────────────

  const toastAnimatedStyle = useAnimatedStyle(() => {
    const enterOffset = (1 - presence.get()) * enterDistance * edgeDirection;

    return {
      opacity: interpolate(presence.get(), [0, 1], [hiddenOpacity, 1]),
      transform: [
        { translateX: swipeX.get() },
        { translateY: animatedDeckTranslateY.get() + enterOffset + swipeY.get() },
        { scale: animatedDeckScale.get() },
      ],
    };
  }, [edgeDirection, enterDistance, hiddenOpacity]);

  const containerStyles = [
    styles.container,
    placement === 'top' ? styles.containerTop : styles.containerBottom,
  ];
  const toastStyles = [
    placement === 'top' ? styles.toastTop : styles.toastBottom,
    toastAnimatedStyle,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      style={containerStyles}
      exiting={EXITING_ANIMATION}
      pointerEvents={isCovered ? 'none' : 'auto'}
      aria-hidden={isCovered}
      onLayout={handleLayout}
    >
      <GestureDetector gesture={swipeGesture}>
        <Animated.View style={toastStyles}>
          <NoticeToast
            text={toast.text}
            highlights={toast.highlights}
            ContentComponent={toast.ContentComponent}
            palette={toast.palette}
            withCloseBtn={withCloseBtn}
            autoFocusCloseBtn={toast.autoFocusCloseBtn}
            closeBtnAccessibilityLabel={toast.closeBtnAccessibilityLabel}
            closeBtnAccessibilityHint={toast.closeBtnAccessibilityHint}
            style={toast.style}
            isLiquidGlassEnabled={isLiquidGlassEnabled}
            onClose={dismiss}
          />
        </Animated.View>
      </GestureDetector>
    </Animated.View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    right: 0,
    left: 0,
  },
  containerTop: {
    top: 0,
  },
  containerBottom: {
    bottom: 0,
  },
  toastTop: {
    transformOrigin: 'bottom',
  },
  toastBottom: {
    transformOrigin: 'top',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ENTER_DISTANCE = 48;

// Glass breaks under animated opacity, so glass toasts slide in from off-screen without fading.
const GLASS_ENTER_DISTANCE = 240;
const SWIPE_ACTIVATION_OFFSET = 10;
const SWIPE_DISMISS_DISTANCE_RATIO = 0.35;
const SWIPE_DISMISS_VELOCITY = 800;

const SWIPE_HORIZONTAL_ACTIVATION_RANGE: ActivationRange = [
  -SWIPE_ACTIVATION_OFFSET,
  SWIPE_ACTIVATION_OFFSET,
];

const SWIPE_UP_ACTIVATION_RANGE: ActivationRange = [
  -SWIPE_ACTIVATION_OFFSET,
  Number.MAX_SAFE_INTEGER,
];

const SWIPE_DOWN_ACTIVATION_RANGE: ActivationRange = [
  -Number.MAX_SAFE_INTEGER,
  SWIPE_ACTIVATION_OFFSET,
];

const ENTER_TIMING = {
  duration: theme.transitions.long,
  easing: Easing.out(Easing.cubic),
  reduceMotion: ReduceMotion.Never,
};

const EXIT_TIMING = {
  duration: theme.transitions.base,
  easing: Easing.in(Easing.cubic),
  reduceMotion: ReduceMotion.Never,
};

const LAYOUT_TIMING = {
  duration: theme.transitions.base,
  easing: Easing.out(Easing.cubic),
};

const EXITING_ANIMATION = FadeOut.duration(theme.transitions.short);

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  toast: Toast;
  placement: 'top' | 'bottom';
  deckOffset: number;
  deckScale: number;
  isCovered: boolean;
  isPaused: boolean;
  isLiquidGlassEnabled: boolean;
  onLayout: (id: string, height: number) => void;
  onDismiss: (toast: Toast) => void;
  onInteractionStart: () => void;
  onInteractionEnd: () => void;
};

type Toast = ComponentProps<typeof NoticeBanner> & { id: string; instanceId?: string };

type SwipeAxis = 'x' | 'y';

type ActivationRange = [number, number];
